import axios from 'axios';
import { store } from '../store';
import { logout, refreshTokenAction } from '../store/slices/authSlice';

/**
 * Phase 2 — session stability helpers (frontend only).
 *
 * Goals:
 *  - Only ONE tab calls POST /auth/refresh at a time (cross-tab lock); other tabs
 *    wait for the rotated tokens (delivered via BroadcastChannel / storage event).
 *  - Proactively refresh the access token a few minutes before it expires.
 *  - The axios 401 interceptor reuses the SAME coordinated refresh (no separate
 *    racing logic).
 *
 * Storage keys are unchanged (`hrms_access_token`, `hrms_refresh_token`). No
 * plain token is ever logged.
 */

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
const ACCESS_KEY = 'hrms_access_token';
const REFRESH_KEY = 'hrms_refresh_token';
const LOCK_KEY = 'hrms_refresh_lock';
const LOCK_TTL_MS = 15_000;            // max time a refresh lock is honoured
const REFRESH_LEAD_MS = 3 * 60_000;    // refresh ~3 min before expiry

const bc: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('hrms-auth') : null;

let inFlight: Promise<string> | null = null;   // in-tab single-flight
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

// ── helpers ────────────────────────────────────────────────────────────────
function decodeExpMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function lockHeld(): boolean {
  const v = localStorage.getItem(LOCK_KEY);
  if (!v) return false;
  const t = Number(v);
  return Number.isFinite(t) && Date.now() - t < LOCK_TTL_MS;
}
function acquireLock() { localStorage.setItem(LOCK_KEY, String(Date.now())); }
function releaseLock() { localStorage.removeItem(LOCK_KEY); }

// Pull tokens from localStorage into the Redux store (used by waiting tabs).
function syncStoreFromStorage() {
  const at = localStorage.getItem(ACCESS_KEY);
  const rt = localStorage.getItem(REFRESH_KEY) || undefined;
  if (at) store.dispatch(refreshTokenAction({ accessToken: at, refreshToken: rt }));
}

// Wait until another tab writes a new access token (storage event or BroadcastChannel).
function waitForTokenChange(timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (tok: string | null) => {
      if (done) return;
      done = true;
      window.removeEventListener('storage', onStorage);
      bc?.removeEventListener('message', onBc as EventListener);
      clearTimeout(to);
      resolve(tok);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCESS_KEY && e.newValue) finish(e.newValue);
    };
    const onBc = (e: MessageEvent) => {
      if (e.data?.type === 'tokens' && e.data.accessToken) finish(e.data.accessToken);
    };
    const to = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('storage', onStorage);
    bc?.addEventListener('message', onBc as EventListener);
  });
}

function currentAccessToken(): string | null {
  return store.getState().auth.accessToken || localStorage.getItem(ACCESS_KEY);
}

// The actual rotation call. Writes both tokens (fires `storage` in other tabs),
// broadcasts, and reschedules. Never logs the tokens.
async function performRefresh(): Promise<string> {
  const refreshToken =
    store.getState().auth.refreshToken || localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new Error('NO_REFRESH_TOKEN');

  const res = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
  const data = res.data?.data ?? res.data;
  const accessToken: string = data.accessToken;
  const newRefresh: string | undefined = data.refreshToken;

  store.dispatch(refreshTokenAction({ accessToken, refreshToken: newRefresh }));
  bc?.postMessage({ type: 'tokens', accessToken, refreshToken: newRefresh });
  scheduleProactiveRefresh();
  return accessToken;
}

// Runs inside whatever exclusive section we hold. If another tab already rotated
// the token while we were queued, adopt that token instead of refreshing again.
async function criticalRefresh(tokenBefore: string | null): Promise<string> {
  const cur = currentAccessToken();
  if (cur && cur !== tokenBefore) { syncStoreFromStorage(); return cur; }
  return performRefresh();
}

// ── coordinated refresh (shared by proactive timer + 401 interceptor) ────────
//  Exclusivity order: Web Locks API (truly atomic cross-tab) → localStorage lock.
export function coordinatedRefresh(): Promise<string> {
  if (inFlight) return inFlight;

  // Captured BEFORE acquiring the lock so a waiting tab can detect that the
  // active tab already rotated the token (then it adopts, no duplicate call).
  const tokenBefore = currentAccessToken();

  inFlight = (async (): Promise<string> => {
    const locks = (typeof navigator !== 'undefined' ? (navigator as any).locks : undefined);
    if (locks?.request) {
      // Preferred: Web Locks API — only one tab runs the callback at a time;
      // others queue and run after, where they'll adopt the rotated token.
      let result: string | undefined;
      await locks.request('hrms-refresh', async () => {
        result = await criticalRefresh(tokenBefore);
      });
      return result as string;
    }

    // Fallback: localStorage lock + wait-for-change (unchanged behavior).
    if (lockHeld()) {
      const tok = await waitForTokenChange(LOCK_TTL_MS);
      if (tok) { syncStoreFromStorage(); return tok; }
      // timed out → refresh ourselves
    }
    acquireLock();
    try {
      return await criticalRefresh(tokenBefore);
    } finally {
      releaseLock();
    }
  })();

  return inFlight.finally(() => { inFlight = null; });
}

// ── proactive refresh scheduling ─────────────────────────────────────────────
export function scheduleProactiveRefresh() {
  clearProactiveRefresh();
  const token = store.getState().auth.accessToken;
  const expMs = decodeExpMs(token);
  if (!expMs) return;

  const msUntilExp = expMs - Date.now();
  // Already expired → let the next request's 401 handle it (avoid a refresh loop).
  if (msUntilExp <= 0) return;

  const delay = Math.max(0, msUntilExp - REFRESH_LEAD_MS);
  proactiveTimer = setTimeout(() => {
    // Swallow errors here; a definitive auth failure is handled by the 401 path.
    coordinatedRefresh().catch(() => { /* no token logged */ });
  }, delay);
}

export function clearProactiveRefresh() {
  if (proactiveTimer) { clearTimeout(proactiveTimer); proactiveTimer = null; }
}

// ── logout (current tab) + propagation ───────────────────────────────────────
export function forceLogout() {
  clearProactiveRefresh();
  bc?.postMessage({ type: 'logout' });
  // logout() removes the LS tokens → `storage` events log sibling tabs out too.
  store.dispatch(logout());
}

function localLogout() {
  clearProactiveRefresh();
  store.dispatch(logout());
}

// ── init (called once after the store is ready) ──────────────────────────────
export function initAuthSession() {
  if (initialized) return;
  initialized = true;

  // Cross-tab token sync + logout propagation via storage events.
  window.addEventListener('storage', (e) => {
    if (e.key === ACCESS_KEY) {
      if (e.newValue) { syncStoreFromStorage(); scheduleProactiveRefresh(); }
      else { localLogout(); } // token removed in another tab (logout)
    }
  });

  // Same via BroadcastChannel where available (faster than storage events).
  bc?.addEventListener('message', (e: MessageEvent) => {
    if (e.data?.type === 'tokens' && e.data.accessToken) {
      syncStoreFromStorage();
      scheduleProactiveRefresh();
    } else if (e.data?.type === 'logout') {
      localLogout();
    }
  });

  // Schedule for the current token and react to login/logout transitions.
  let lastToken = store.getState().auth.accessToken;
  scheduleProactiveRefresh();
  store.subscribe(() => {
    const t = store.getState().auth.accessToken;
    if (t && t !== lastToken) { lastToken = t; scheduleProactiveRefresh(); }
    else if (!t && lastToken) { lastToken = null; clearProactiveRefresh(); }
  });
}
